import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { InPersonSessionResponse } from "models/interfaces";
import { RootState } from "utils/store";

/**
 * Client-side state for an active in-person signing session. The flow advances
 * each signer by hard-navigating the token-scoped signing surface
 * (`window.location.assign`), so this must survive a full reload — it cannot
 * live in memory only. Kept small and serializable for that reason.
 * `requestId` is the envelope id — needed to call the host `next` / `return`
 * endpoints. Cleared on end.
 *
 * It holds a live `requestToken` plus the walk-in signer's name/email on what
 * is by design a shared/kiosk device, so `utils/store.ts` deliberately carves
 * this one slice out of the blanket localStorage persistence and keeps it in
 * sessionStorage instead: it survives the hand-off navigations but dies with
 * the tab. `startedAt` additionally stamps every session so
 * `pruneStaleInPersonSession` can drop it on rehydrate once
 * `IN_PERSON_SESSION_TTL_MS` has elapsed, covering a tab left open for hours.
 */
export interface InPersonSessionState {
  active: boolean;
  requestId: string;
  requestToken: string;
  /** Signed callback key bound to `requestToken` — see `InPersonSessionResponse.requestKey`. */
  requestKey: string;
  signerName: string;
  /** Display only — empty when the signer gave no email. Never an identifier. */
  signerEmail: string;
  /** Opaque identity handle; what `return` echoes back. See the hand-off spec. */
  signerRef: string;
  /** Numeric participant id — what the "email me a copy" call needs. */
  signerId: number;
  order: number;
  /** The host tapped "I've handed it over" — reveals the signing surface. */
  handoffAcknowledged: boolean;
  /** Epoch ms of the last start/advance — drives the persistence TTL. */
  startedAt: number;
  /**
   * Set only when this session was launched from the public host-link page
   * (`/in-person/host?token=`) rather than the logged-in launch button — the
   * bearer credential every subsequent next/return call in the session must
   * use instead of `requestId`. Empty for the authed flow.
   */
  hostToken: string;
  /**
   * Signed callback key bound to `hostToken` (see `DocumentSignHelper.inPersonHostLinkUrl`).
   * Persisted alongside `hostToken` rather than read from the current URL, because by the
   * time a host-link call fires from the signing surface the browser has already navigated
   * away to the signer's `doSign` page, whose URL carries the signer's key, not the host's.
   */
  hostKey: string;
}

const initialState: InPersonSessionState = {
  active: false,
  requestId: "",
  requestToken: "",
  requestKey: "",
  signerName: "",
  signerEmail: "",
  signerRef: "",
  signerId: 0,
  order: 0,
  handoffAcknowledged: false,
  startedAt: 0,
  hostToken: "",
  hostKey: "",
};

/** A hand-off runs in minutes; anything older is abandoned, not resumable. */
export const IN_PERSON_SESSION_TTL_MS = 2 * 60 * 60 * 1000;

/**
 * Drop an expired in-person session out of the rehydrated state, so the
 * reducer falls back to `initialState`. Called by `utils/store.ts` before the
 * store is created.
 */
export const pruneStaleInPersonSession = (persistedState: any) => {
  const session = persistedState?.inPersonSession;
  if (!session) return persistedState;
  const startedAt = Number(session.startedAt) || 0;
  if (Date.now() - startedAt > IN_PERSON_SESSION_TTL_MS) {
    delete persistedState.inPersonSession;
  }
  return persistedState;
};

const applyResponse = (
  state: InPersonSessionState,
  response: InPersonSessionResponse,
) => {
  state.requestToken = response.requestToken ?? "";
  state.requestKey = response.requestKey ?? "";
  state.signerName = response.signerName ?? "";
  state.signerEmail = response.signerEmail ?? "";
  state.signerRef = response.signerRef ?? "";
  state.signerId = response.signerId ?? 0;
  state.order = response.order ?? 0;
  state.handoffAcknowledged = false;
  state.startedAt = Date.now();
};

export const inPersonSessionSlice = createSlice({
  name: "inPersonSession",
  initialState,
  reducers: {
    // Host launched a session on an envelope.
    beginInPersonSession: (
      state,
      action: PayloadAction<{
        requestId: string;
        response: InPersonSessionResponse;
        /** Present for a session launched from the public host-link page. */
        hostToken?: string;
        /** The signed key bound to `hostToken` — present exactly when `hostToken` is. */
        hostKey?: string;
      }>,
    ) => {
      state.active = true;
      state.requestId = action.payload.requestId;
      state.hostToken = action.payload.hostToken ?? "";
      state.hostKey = action.payload.hostKey ?? "";
      applyResponse(state, action.payload.response);
    },
    // Advance to the next actionable in-person signer (envelope unchanged).
    advanceInPersonSession: (
      state,
      action: PayloadAction<InPersonSessionResponse>,
    ) => {
      state.active = true;
      applyResponse(state, action.payload);
    },
    // Host tapped the hand-off "Continue" affordance.
    acknowledgeHandoff: (state) => {
      state.handoffAcknowledged = true;
    },
    // Session finished / abandoned — clear the client lock.
    endInPersonSession: () => initialState,
  },
});

export const {
  beginInPersonSession,
  advanceInPersonSession,
  acknowledgeHandoff,
  endInPersonSession,
} = inPersonSessionSlice.actions;

export const selectInPersonSession = (state: RootState) =>
  state.inPersonSession;

export default inPersonSessionSlice.reducer;
